import { useState, useEffect, useCallback, useMemo } from 'react';
import { Employee, EmployeeRole } from '../models/employee';
import {
  getEmployees,
  GetEmployeesParams,
} from '../services/api/employeeServices';
import useAppSnackbar from './useAppSnackbar';
import { DEFAULT_PAGE_SIZE } from '../constants';

interface GetEmployeesReturn {
  employees: Employee[];
  isLoading: boolean;
  refetch: (parameters: GetEmployeesParams) => void;
  isEmpty: boolean;
  hasMore: boolean;
  updateEmployees: (employees: Employee[]) => void;
}

const defaultParams = {
  take: DEFAULT_PAGE_SIZE,
  includeDeleted: false,
  includeInactive: false,
  skip: 0,
  roleId: null,
};

export function useGetEmployees(): GetEmployeesReturn {
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { onErrorSnackbar } = useAppSnackbar();

  const fetchEmployees = useCallback(
    (parameters?: GetEmployeesParams) => {
      const params = { ...defaultParams, ...parameters };
      setIsLoading(true);

      getEmployees(params)
        .then((resp) => {
          if (params.skip) {
            setEmployees((prevState) => [...prevState, ...resp.items]);
          } else {
            setEmployees([...resp.items]);
          }

          const doesHaveMore =
            resp.totalCount - resp.items.length - params.skip > 0;

          setHasMore(doesHaveMore);
        })
        .catch(onErrorSnackbar)
        .finally(() => {
          setIsLoading(false);
        });
    },
    [onErrorSnackbar]
  );

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const isEmpty = useMemo(
    () => !employees.length && !isLoading,
    [employees.length, isLoading]
  );

  const updateEmployees = useCallback((updatedEmployees: Employee[]) => {
    setEmployees(updatedEmployees);
  }, []);

  return {
    employees,
    isLoading,
    isEmpty,
    hasMore,
    refetch: fetchEmployees,
    updateEmployees,
  };
}
